import { useState } from 'react'
import { useApiPost, useApiQuery } from '@/api/main/query'
import { useDebounce, useToast } from '@/shared/hooks'
import { useQueryClient } from '@tanstack/react-query'
import { requisiteAPI, RequisiteModel, RequisiteStatuses } from '@/features/requisites'
import { PaginatedCollection } from '@/api/types'

export const useFarmRequisites = (farmId: string | undefined) => {
  const { addToast } = useToast()
  const queryClient = useQueryClient()

  const [modalVisible, setModalVisible] = useState(false)
  const [shouldLoadUnlinked, setShouldLoadUnlinked] = useState(false)
  const [selectedStatus, setSelectedStatus] = useState<string | number | null>('all')
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 500)

  const status =
    selectedStatus === 'active'
      ? RequisiteStatuses.Active
      : selectedStatus === 'disabled'
        ? RequisiteStatuses.Disabled
        : selectedStatus === 'blocked'
          ? 4
          : 0

  const { data, isPending } = useApiQuery<PaginatedCollection<RequisiteModel[]>>(
    ['unlinkedRequisites', status, debouncedSearch],
    requisiteAPI.getUnlinkedRequisites,
    {
      status,
      textFilter: debouncedSearch,
    },
    {
      enabled: shouldLoadUnlinked,
    }
  )

  const { mutate: attachToFarm, isPending: isAttaching } = useApiPost<
    void,
    { farmId: string; requisitIds: string[] }
  >(requisiteAPI.attachToFarm)

  const openModal = () => {
    setShouldLoadUnlinked(true)
    setModalVisible(true)
  }

  const closeModal = () => {
    setShouldLoadUnlinked(false)
    setModalVisible(false)
  }

  const handleAttach = (ids: string[]) => {
    if (!farmId) {
      addToast('Ошибка!', 'ID фермы отсутствует.', 'error')
      return
    }

    attachToFarm(
      { farmId, requisitIds: ids },
      {
        onSuccess: () => {
          addToast('Успех!', 'Реквизит(ы) привязаны к ферме.', 'success')
          queryClient.invalidateQueries({ queryKey: ['requisites'], exact: false })
          queryClient.invalidateQueries({ queryKey: ['unlinkedRequisites'], exact: false })
          closeModal()
        },
        onError: (error) => {
          addToast('Ошибка!', error.message, 'error')
        },
      }
    )
  }

  return {
    requisites: data?.items ?? [],
    isLoading: isPending || isAttaching,
    modalVisible,
    openModal,
    closeModal,
    selectedStatus,
    setSelectedStatus,
    setSearch,
    handleAttach,
  }
}
